import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, ChevronUp, Save, RotateCcw,
  AlertTriangle, Info, TrendingUp, Eye,
} from 'lucide-react';
import { cn } from '../utils/cn';
import RankingBadge, { parseRanking } from '../components/shared/RankingBadge';

// ─── KPI Meta (27 KPI theo Phụ lục II v3.1) ─────────────────────────────────

interface KpiMeta {
  code: string;
  tc: string;
  name: string;
  weight: number;    // % trọng số trong TC
  isNaCapable: boolean;
  dataSource: 'TTDLQG_AUTO' | 'SELF_ASSESS' | 'BOTH';
  thresholds: { score: 10 | 7 | 5 | 3 | 0; label: string }[];
}

const TC_META: Record<string, { name: string; weight: number; color: string }> = {
  TC1: { name: 'Danh mục & Công bố dữ liệu', weight: 15, color: '#6366f1' },
  TC2: { name: 'Kết nối & Chia sẻ dữ liệu',  weight: 10, color: '#8b5cf6' },
  TC3: { name: 'Dịch vụ công trực tuyến',     weight: 10, color: '#06b6d4' },
  TC4: { name: 'Chất lượng dữ liệu',          weight: 15, color: '#10b981' },
  TC5: { name: 'An ninh thông tin',            weight: 15, color: '#f43f5e' },
  TC6: { name: 'Kết nối NDOP/C12',            weight: 10, color: '#f59e0b' },
  TC7: { name: 'Kiểm toán nội bộ & Tuân thủ', weight: 15, color: '#a855f7' },
  TC8: { name: 'Hạ tầng kỹ thuật & Vận hành', weight: 10, color: '#0ea5e9' },
};

const KPI_META: KpiMeta[] = [
  // TC1
  { code: 'TC1a', tc: 'TC1', name: 'Tỷ lệ ban hành danh mục dữ liệu',         weight: 30, isNaCapable: false, dataSource: 'TTDLQG_AUTO', thresholds: [{score:10,label:'≥95%'},{score:7,label:'80–94%'},{score:5,label:'60–79%'},{score:3,label:'40–59%'},{score:0,label:'<40%'}] },
  { code: 'TC1b', tc: 'TC1', name: 'Cấp độ an toàn thông tin đạt yêu cầu',     weight: 40, isNaCapable: false, dataSource: 'SELF_ASSESS',  thresholds: [{score:10,label:'Cấp 3/4'},{score:7,label:'Cấp 2'},{score:5,label:'Cấp 1'},{score:0,label:'Chưa cấp'}] },
  { code: 'TC1c', tc: 'TC1', name: 'Tỷ lệ CSDL kết nối NDOP',                  weight: 30, isNaCapable: false, dataSource: 'SELF_ASSESS',  thresholds: [{score:10,label:'100%'},{score:7,label:'80–99%'},{score:5,label:'60–79%'},{score:0,label:'<60%'}] },
  // TC2
  { code: 'TC2a', tc: 'TC2', name: 'Tần suất đồng bộ dữ liệu',                 weight: 40, isNaCapable: false, dataSource: 'SELF_ASSESS',  thresholds: [{score:10,label:'Real-time'},{score:7,label:'Hàng ngày'},{score:5,label:'Hàng tuần'},{score:0,label:'Thủ công'}] },
  { code: 'TC2b', tc: 'TC2', name: 'Kết nối API chuẩn NDOP',                   weight: 30, isNaCapable: true,  dataSource: 'TTDLQG_AUTO', thresholds: [{score:10,label:'≥99%'},{score:7,label:'95–98%'},{score:5,label:'90–94%'},{score:0,label:'<90%'}] },
  { code: 'TC2c', tc: 'TC2', name: 'Chất lượng giao thức truyền dữ liệu',      weight: 30, isNaCapable: false, dataSource: 'SELF_ASSESS',  thresholds: [{score:10,label:'Đạt chuẩn'},{score:5,label:'Một phần'},{score:0,label:'Không đạt'}] },
  // TC3
  { code: 'TC3a', tc: 'TC3', name: 'Tỷ lệ DVCTT mức 3-4',                     weight: 50, isNaCapable: true,  dataSource: 'SELF_ASSESS',  thresholds: [{score:10,label:'≥80%'},{score:7,label:'60–79%'},{score:5,label:'40–59%'},{score:0,label:'<40%'}] },
  { code: 'TC3b', tc: 'TC3', name: 'Tích hợp CSDL quốc gia',                  weight: 50, isNaCapable: true,  dataSource: 'SELF_ASSESS',  thresholds: [{score:10,label:'Đầy đủ'},{score:7,label:'Một phần'},{score:0,label:'Chưa'}] },
  // TC4
  { code: 'TC4a', tc: 'TC4', name: 'Tỷ lệ dữ liệu đạt chất lượng',            weight: 40, isNaCapable: false, dataSource: 'TTDLQG_AUTO', thresholds: [{score:10,label:'≥95%'},{score:7,label:'80–94%'},{score:5,label:'60–79%'},{score:0,label:'<60%'}] },
  { code: 'TC4b', tc: 'TC4', name: 'Tỷ lệ trùng lặp dữ liệu',                 weight: 30, isNaCapable: false, dataSource: 'TTDLQG_AUTO', thresholds: [{score:10,label:'<1%'},{score:7,label:'1–5%'},{score:5,label:'5–10%'},{score:0,label:'>10%'}] },
  { code: 'TC4c', tc: 'TC4', name: 'Tuân thủ từ điển dữ liệu quốc gia',       weight: 30, isNaCapable: false, dataSource: 'TTDLQG_AUTO', thresholds: [{score:10,label:'≥95%'},{score:7,label:'80–94%'},{score:5,label:'60–79%'},{score:0,label:'<60%'}] },
  // TC5
  { code: 'TC5a', tc: 'TC5', name: 'Lỗ hổng CVSS≥7.0 chưa vá (số lượng)',     weight: 30, isNaCapable: false, dataSource: 'TTDLQG_AUTO', thresholds: [{score:10,label:'0'},{score:7,label:'1–2'},{score:5,label:'3–5'},{score:0,label:'>5'}] },
  { code: 'TC5b', tc: 'TC5', name: 'MTTR trung bình (giờ)',                    weight: 30, isNaCapable: false, dataSource: 'TTDLQG_AUTO', thresholds: [{score:10,label:'≤1h'},{score:7,label:'≤4h'},{score:5,label:'≤8h'},{score:3,label:'≤24h'},{score:0,label:'>24h'}] },
  { code: 'TC5c', tc: 'TC5', name: 'Kiểm thử xâm nhập định kỳ',               weight: 40, isNaCapable: true,  dataSource: 'SELF_ASSESS',  thresholds: [{score:10,label:'≥2 lần/năm'},{score:7,label:'1 lần/năm'},{score:0,label:'Chưa thực hiện'}] },
  // TC6
  { code: 'TC6a', tc: 'TC6', name: 'Tỷ lệ kết nối thành công NDOP',           weight: 40, isNaCapable: false, dataSource: 'TTDLQG_AUTO', thresholds: [{score:10,label:'≥99.9%'},{score:7,label:'99–99.9%'},{score:5,label:'95–99%'},{score:0,label:'<95%'}] },
  { code: 'TC6b', tc: 'TC6', name: 'MTTR sự cố kết nối (giờ)',                weight: 30, isNaCapable: false, dataSource: 'TTDLQG_AUTO', thresholds: [{score:10,label:'≤1h'},{score:7,label:'≤4h'},{score:5,label:'≤8h'},{score:0,label:'>8h'}] },
  { code: 'TC6c', tc: 'TC6', name: 'Giám sát 24/7 kết nối SOC',               weight: 30, isNaCapable: false, dataSource: 'SELF_ASSESS',  thresholds: [{score:10,label:'Có'},{score:0,label:'Không'}] },
  // TC7
  { code: 'TC7a', tc: 'TC7', name: 'Thực hiện kiểm toán nội bộ định kỳ',      weight: 40, isNaCapable: false, dataSource: 'SELF_ASSESS',  thresholds: [{score:10,label:'≥2 lần/năm'},{score:7,label:'1 lần/năm'},{score:0,label:'Chưa'}] },
  { code: 'TC7b', tc: 'TC7', name: 'Vi phạm nghiêm trọng được phát hiện',     weight: 30, isNaCapable: false, dataSource: 'SELF_ASSESS',  thresholds: [{score:10,label:'0'},{score:5,label:'1–2'},{score:0,label:'>2'}] },
  { code: 'TC7c', tc: 'TC7', name: 'Tỷ lệ khắc phục kiến nghị kỳ trước',     weight: 30, isNaCapable: false, dataSource: 'SELF_ASSESS',  thresholds: [{score:10,label:'100%'},{score:7,label:'80–99%'},{score:5,label:'60–79%'},{score:0,label:'<60%'}] },
  // TC8
  { code: 'TC8a', tc: 'TC8', name: 'Hạ tầng đáp ứng SLA 99.9%',              weight: 40, isNaCapable: false, dataSource: 'SELF_ASSESS',  thresholds: [{score:10,label:'Đạt'},{score:5,label:'Một phần'},{score:0,label:'Không đạt'}] },
  { code: 'TC8b', tc: 'TC8', name: 'Sao lưu và phục hồi dữ liệu',            weight: 30, isNaCapable: false, dataSource: 'SELF_ASSESS',  thresholds: [{score:10,label:'Tự động + kiểm thử'},{score:7,label:'Tự động'},{score:5,label:'Thủ công'},{score:0,label:'Chưa'}] },
  { code: 'TC8c', tc: 'TC8', name: 'Quản lý thay đổi (Change Management)',    weight: 30, isNaCapable: false, dataSource: 'SELF_ASSESS',  thresholds: [{score:10,label:'Có quy trình chuẩn'},{score:5,label:'Một phần'},{score:0,label:'Chưa'}] },
  // Extra kpi: TC3 N/A flag (internal)
  { code: 'TC3NA', tc: 'TC3', name: 'Không có DVCTT (xác nhận N/A)',          weight: 0,  isNaCapable: true,  dataSource: 'SELF_ASSESS',  thresholds: [] },
];

// ─── MOCK: State mẫu cho phiên kiểm toán ─────────────────────────────────────

interface KpiState {
  score: number | null;
  isNa: boolean;
  selfDeclared: string;
  autoCollected: number | null;
  dirty: boolean;
}

function buildDefaultState(): Record<string, KpiState> {
  const state: Record<string, KpiState> = {};
  for (const kpi of KPI_META) {
    state[kpi.code] = { score: null, isNa: false, selfDeclared: '', autoCollected: null, dirty: false };
  }
  return state;
}

// ─── COMPONENTS ──────────────────────────────────────────────────────────────

function ScorePill({ score, className }: { score: number | null; className?: string }) {
  if (score === null) return <span className={cn('text-[10px] text-slate-400 font-light', className)}>—</span>;
  const color = score >= 7 ? 'text-emerald-600' : score >= 5 ? 'text-amber-600' : 'text-rose-600';
  return <span className={cn('text-sm font-heading font-light', color, className)}>{score.toFixed(1)}</span>;
}

function ThresholdGuide({ thresholds }: { thresholds: KpiMeta['thresholds'] }) {
  if (thresholds.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {thresholds.map(t => (
        <span key={t.score} className="inline-flex items-center gap-1 text-[9px] font-light px-2 py-0.5 rounded-full bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/10">
          <span className={cn('font-medium', t.score >= 7 ? 'text-emerald-600' : t.score >= 5 ? 'text-amber-500' : 'text-rose-500')}>{t.score}đ</span>
          {t.label}
        </span>
      ))}
    </div>
  );
}

function TcSection({
  tcCode, kpis, values, onChange, expanded, onToggle,
}: {
  tcCode: string;
  kpis: KpiMeta[];
  values: Record<string, KpiState>;
  onChange: (code: string, field: keyof KpiState, val: unknown) => void;
  expanded: boolean;
  onToggle: () => void;
}) {
  const meta = TC_META[tcCode];
  const tcKpis = kpis.filter(k => k.tc === tcCode && k.code !== 'TC3NA');

  // Tính điểm TC tạm
  const filledCount  = tcKpis.filter(k => values[k.code]?.score !== null || values[k.code]?.isNa).length;
  const totalWeight  = tcKpis.reduce((s, k) => s + k.weight, 0);
  const weightedSum  = tcKpis.reduce((s, k) => {
    const v = values[k.code];
    if (v?.isNa) return s;
    if (v?.score === null) return s;
    return s + (v.score * k.weight);
  }, 0);
  const tcScore = totalWeight > 0 && filledCount > 0 ? (weightedSum / 100) : null;

  return (
    <div className="glass-panel overflow-hidden shadow-sm">
      {/* TC Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-2 h-10 rounded-full" style={{ backgroundColor: meta.color }} />
          <div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-heading font-medium text-slate-950 dark:text-white">{tcCode}</span>
              <span className="text-[10px] text-slate-500 font-light">Trọng số {meta.weight}%</span>
              <span className="text-[10px] text-slate-400 font-light">({filledCount}/{tcKpis.length} KPI đã nhập)</span>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-400 font-light mt-0.5">{meta.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {tcScore !== null && (
            <div className="text-right">
              <p className="text-[9px] text-slate-400 font-light mb-0.5">Điểm tạm</p>
              <span className={cn(
                'text-xl font-heading font-light',
                tcScore >= 7 ? 'text-emerald-600' : tcScore >= 5 ? 'text-amber-500' : 'text-rose-500',
              )}>
                {tcScore.toFixed(2)}
              </span>
            </div>
          )}
          {expanded ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
        </div>
      </button>

      {/* KPI Rows */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="border-t border-slate-100 dark:border-white/5 divide-y divide-slate-50 dark:divide-white/5">
              {tcKpis.map(kpi => {
                const v = values[kpi.code] ?? { score: null, isNa: false, selfDeclared: '', autoCollected: null, dirty: false };
                const isAuto = kpi.dataSource === 'TTDLQG_AUTO';

                return (
                  <div key={kpi.code} className={cn(
                    'p-6 grid grid-cols-12 gap-4 items-start hover:bg-slate-50/50 dark:hover:bg-white/5 transition-colors',
                    v.dirty && 'bg-amber-50/30 dark:bg-amber-500/5',
                  )}>
                    {/* KPI Info */}
                    <div className="col-span-12 lg:col-span-5">
                      <div className="flex items-center gap-2 mb-1">
                        <code className="text-[10px] font-mono font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
                          {kpi.code}
                        </code>
                        {isAuto && (
                          <span className="text-[9px] font-light bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 px-2 py-0.5 rounded-full tracking-wide">
                            Tự động
                          </span>
                        )}
                        {kpi.isNaCapable && (
                          <span className="text-[9px] font-light bg-slate-100 dark:bg-white/5 text-slate-500 border border-slate-200 dark:border-white/10 px-2 py-0.5 rounded-full">
                            N/A được
                          </span>
                        )}
                        <span className="text-[9px] text-slate-400 font-light">w={kpi.weight}%</span>
                      </div>
                      <p className="text-xs font-light text-slate-700 dark:text-slate-300">{kpi.name}</p>
                      <ThresholdGuide thresholds={kpi.thresholds} />
                    </div>

                    {/* Auto collected value */}
                    <div className="col-span-12 lg:col-span-2 text-center">
                      <p className="text-[9px] text-slate-400 font-light mb-1">Tự động</p>
                      {v.autoCollected !== null ? (
                        <ScorePill score={v.autoCollected} />
                      ) : (
                        <span className="text-[10px] text-slate-300 dark:text-white/20">—</span>
                      )}
                    </div>

                    {/* Score input */}
                    <div className="col-span-12 lg:col-span-3">
                      <p className="text-[9px] text-slate-400 font-light mb-1.5">
                        {isAuto ? 'Điểm xác nhận TTDLQG' : 'Điểm tự khai'}
                      </p>
                      <div className="flex items-center gap-2">
                        {kpi.isNaCapable && (
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={v.isNa}
                              onChange={e => onChange(kpi.code, 'isNa', e.target.checked)}
                              className="w-3.5 h-3.5 accent-primary"
                              disabled={isAuto}
                            />
                            <span className="text-[10px] text-slate-500 font-light">N/A</span>
                          </label>
                        )}
                        {!v.isNa && (
                          <select
                            value={v.score ?? ''}
                            onChange={e => onChange(kpi.code, 'score', e.target.value === '' ? null : parseFloat(e.target.value))}
                            disabled={isAuto}
                            className={cn(
                              'flex-1 text-xs font-light rounded-xl px-3 py-2 border bg-white dark:bg-black/20 text-slate-700 dark:text-slate-300',
                              isAuto
                                ? 'border-slate-100 dark:border-white/5 opacity-60 cursor-not-allowed'
                                : 'border-slate-200 dark:border-white/10 focus:border-primary focus:outline-none cursor-pointer',
                            )}
                          >
                            <option value="">— Chọn —</option>
                            {[10, 7, 5, 3, 0].map(s => (
                              <option key={s} value={s}>{s} điểm</option>
                            ))}
                          </select>
                        )}
                        {v.isNa && (
                          <span className="flex-1 text-xs font-light text-slate-400 px-3 py-2 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5 text-center">
                            N/A
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Current score */}
                    <div className="col-span-12 lg:col-span-2 text-center">
                      <p className="text-[9px] text-slate-400 font-light mb-1">Điểm</p>
                      <ScorePill score={v.isNa ? null : v.score} />
                      {v.dirty && <span className="block text-[8px] text-amber-500 mt-0.5">Chưa lưu</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function KpiFormPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [values, setValues] = useState<Record<string, KpiState>>(buildDefaultState);
  const [expandedTc, setExpandedTc] = useState<Record<string, boolean>>({ TC1: true });
  const [saving, setSaving] = useState(false);
  const [previewScore, setPreviewScore] = useState<number | null>(null);
  const [previewRanking, setPreviewRanking] = useState<string | null>(null);

  const tcCodes = Object.keys(TC_META);

  // Tính tổng điểm preview nhanh (client-side estimation)
  const clientEstimate = useMemo(() => {
    const TC_WEIGHTS: Record<string, number> = { TC1:15,TC2:10,TC3:10,TC4:15,TC5:15,TC6:10,TC7:15,TC8:10 };
    let raw = 0;
    for (const tc of tcCodes) {
      const kpis = KPI_META.filter(k => k.tc === tc && k.code !== 'TC3NA');
      const totalW = kpis.reduce((s, k) => s + k.weight, 0);
      let tcW = 0;
      for (const kpi of kpis) {
        const v = values[kpi.code];
        if (!v || v.isNa || v.score === null) continue;
        tcW += (v.score * kpi.weight) / totalW;
      }
      raw += tcW * TC_WEIGHTS[tc] / 100;
    }
    return +(raw * 10).toFixed(2);
  }, [values]);

  const handleChange = (code: string, field: keyof KpiState, val: unknown) => {
    setValues(prev => ({
      ...prev,
      [code]: { ...prev[code], [field]: val, dirty: true },
    }));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    // TODO: call API PUT /assessments/:id/kpi-measurements/:kpiCode for each dirty KPI
    await new Promise(r => setTimeout(r, 600));
    setValues(prev => Object.fromEntries(
      Object.entries(prev).map(([k, v]) => [k, { ...v, dirty: false }]),
    ));
    setSaving(false);
  };

  const handlePreview = async () => {
    // TODO: call POST /assessments/:id/preview-score
    await new Promise(r => setTimeout(r, 400));
    setPreviewScore(clientEstimate);
    setPreviewRanking(
      clientEstimate >= 80 ? 'A' :
      clientEstimate >= 70 ? 'B_TOT' :
      clientEstimate >= 60 ? 'B_DAT' : 'C',
    );
  };

  const dirtyCount = Object.values(values).filter(v => v.dirty).length;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading text-slate-950 dark:text-white tracking-tight">
            Nhập điểm 27 KPI
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 font-light mt-1">
            Phụ lục II v3.1 / NĐ 278/2025 · Phiên: <code className="text-primary text-xs">{sessionId ?? '—'}</code>
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            onClick={handlePreview}
            className="btn-outline flex items-center gap-2"
          >
            <Eye size={16} />
            Tính điểm sơ bộ
          </button>
          <button
            onClick={handleSaveAll}
            disabled={saving || dirtyCount === 0}
            className="btn-futuristic flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <RotateCcw size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Đang lưu…' : `Lưu (${dirtyCount})`}
          </button>
        </div>
      </div>

      {/* Preview score card */}
      <AnimatePresence>
        {previewScore !== null && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="glass-panel p-6 flex items-center justify-between"
          >
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <TrendingUp size={20} className="text-primary" />
                <div>
                  <p className="text-[10px] text-slate-500 font-light tracking-wide">ĐIỂM SƠ BỘ (PREVIEW)</p>
                  <p className="text-3xl font-heading font-light text-slate-950 dark:text-white">{previewScore}</p>
                </div>
              </div>
              <RankingBadge ranking={parseRanking(previewRanking)} score={previewScore} variant="card" showScore={false} />
            </div>
            <div className="flex items-center gap-2 text-[10px] text-slate-400 font-light">
              <Info size={14} />
              Đây là điểm ước tính. Điểm chính thức do TTDLQG xác nhận.
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Client estimate bar */}
      <div className="glass-panel p-4 flex items-center gap-4">
        <div className="flex-1">
          <div className="flex justify-between text-[10px] text-slate-500 font-light mb-1.5">
            <span>Ước tính điểm client (chưa lưu)</span>
            <span className="font-medium text-slate-700 dark:text-white">{clientEstimate} / 100</span>
          </div>
          <div className="h-2 bg-slate-100 dark:bg-white/10 rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500',
                clientEstimate >= 80 ? 'bg-emerald-500' :
                clientEstimate >= 70 ? 'bg-sky-500' :
                clientEstimate >= 60 ? 'bg-blue-500' : 'bg-rose-500',
              )}
              style={{ width: `${Math.min(clientEstimate, 100)}%` }}
            />
          </div>
        </div>
        <RankingBadge
          ranking={parseRanking(
            clientEstimate >= 80 ? 'A' :
            clientEstimate >= 70 ? 'B_TOT' :
            clientEstimate >= 60 ? 'B_DAT' : 'C',
          )}
          size="sm"
        />
      </div>

      {/* TC sections */}
      <div className="space-y-4">
        {tcCodes.map(tc => (
          <TcSection
            key={tc}
            tcCode={tc}
            kpis={KPI_META}
            values={values}
            onChange={handleChange}
            expanded={expandedTc[tc] ?? false}
            onToggle={() => setExpandedTc(prev => ({ ...prev, [tc]: !prev[tc] }))}
          />
        ))}
      </div>

      {/* Sticky save footer */}
      {dirtyCount > 0 && (
        <div className="fixed bottom-6 right-6 z-50">
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className="glass-panel px-6 py-4 shadow-2xl flex items-center gap-4 border border-amber-500/20"
          >
            <AlertTriangle size={18} className="text-amber-500" />
            <span className="text-sm font-light text-slate-700 dark:text-slate-300">
              {dirtyCount} KPI chưa được lưu
            </span>
            <button onClick={handleSaveAll} disabled={saving} className="btn-futuristic text-sm py-2">
              {saving ? 'Đang lưu…' : 'Lưu ngay'}
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
